import React from "react";
import { FiArrowRight, FiChevronRight, FiUsers } from "react-icons/fi";
import { Link } from "react-router-dom";

const WIDTHS = [
  [90, "w-full"],
  [75, "w-10/12"],
  [60, "w-3/4"],
  [40, "w-1/2"],
  [25, "w-1/3"],
  [0, "w-1/4"],
];

function getWidthClass(percent) {
  return WIDTHS.find(([minimum]) => percent >= minimum)?.[1] || "w-1/4";
}

function MemberAvatar({ name, photoURL }) {
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  if (photoURL) {
    return (
      <img
        alt={`${name} profile`}
        className="h-9 w-9 shrink-0 rounded-full border-2 border-white object-cover shadow-sm"
        src={photoURL}
      />
    );
  }

  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700">
      {initials || "?"}
    </span>
  );
}

export function HomeTeamWorkload({ workloadData = [] }) {
  const maxCount = Math.max(...workloadData.map((member) => member.count), 0);

  return (
    <section className="relative overflow-hidden rounded-[26px] border border-slate-100 bg-white shadow-[0_12px_32px_rgba(15,23,42,0.08)]">
      <span className="absolute inset-y-0 left-0 w-1.5 bg-[#6748d9]" />
      <Link
        aria-label="Open team workload report"
        className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-slate-100 bg-white text-slate-950 shadow-sm transition hover:bg-slate-50"
        to="/tasks/reports/team-workload"
      >
        <FiChevronRight className="h-6 w-6" />
      </Link>

      <div className="px-6 py-7 sm:px-9 sm:py-8">
        <div className="flex items-start justify-between gap-4">
          <div className="flex h-14 w-14 sm:h-20 sm:w-20 shrink-0 items-center justify-center rounded-2xl sm:rounded-[24px] bg-[#eeeaff]">
            <FiUsers className="h-7 w-7 sm:h-11 sm:w-11 text-[#6748d9]" strokeWidth={2} />
          </div>
          <div className="mt-2 flex h-14 items-end gap-1.5 sm:h-20 sm:gap-2 pr-14 sm:pr-20" aria-hidden="true">
            <span className="h-5 w-4 rounded-t-lg bg-violet-100 sm:h-7 sm:w-6 sm:rounded-t-xl" />
            <span className="h-8 w-4 rounded-t-lg bg-violet-200 sm:h-11 sm:w-6 sm:rounded-t-xl" />
            <span className="h-11 w-4 rounded-t-lg bg-violet-300 sm:h-16 sm:w-6 sm:rounded-t-xl" />
            <span className="h-14 w-4 rounded-t-lg bg-[#6748d9] sm:h-20 sm:w-6 sm:rounded-t-xl" />
          </div>
        </div>

        <h2 className="m-0 mt-4 sm:mt-5 text-xl sm:text-2xl md:text-[29px] font-bold leading-tight tracking-tight text-slate-950 whitespace-nowrap">
          Team Workload
        </h2>
        <p className="mb-0 mt-2 max-w-sm text-sm sm:text-base md:text-lg font-medium leading-6 text-slate-600">
          See how much work others are loaded with.
        </p>

        {workloadData.length === 0 ? (
          <div className="mt-7 rounded-2xl bg-violet-50 px-5 py-6 text-center text-sm font-medium text-slate-500">
            No active team workload to display.
          </div>
        ) : (
          <ul className="m-0 mt-7 space-y-4 p-0">
            {workloadData.slice(0, 5).map((member) => {
              const percentage = Math.max(
                12,
                Math.round((member.count / maxCount) * 100)
              );

              return (
                <li className="grid grid-cols-[minmax(0,9.5rem)_minmax(4rem,1fr)_2.75rem] items-center gap-3 sm:grid-cols-[minmax(0,12rem)_minmax(6rem,1fr)_3rem]" key={member.name}>
                  <div className="flex min-w-0 items-center gap-3">
                    <MemberAvatar name={member.name} photoURL={member.photoURL} />
                    <span className="truncate text-sm font-medium text-slate-800 sm:text-base">{member.name}</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-violet-100">
                    <span className={`block h-full rounded-full bg-[#6748d9] ${getWidthClass(percentage)}`} />
                  </div>
                  <span className="text-right text-sm font-bold text-[#6748d9] sm:text-base">{percentage}%</span>
                </li>
              );
            })}
          </ul>
        )}

        <Link
          className="mt-8 inline-flex items-center gap-5 rounded-2xl border border-[#6748d9] px-7 py-3 text-lg font-semibold text-[#6748d9] no-underline transition hover:bg-violet-50"
          to="/tasks/reports/team-workload"
        >
          View Workload <FiArrowRight className="h-5 w-5" />
        </Link>
      </div>
    </section>
  );
}
